import { useState, useRef, useEffect } from "react";
import ReactMarkdown from "react-markdown";
import {
  Mic, Square, Volume2, Copy, Check,
  Send, Mail, X, Bot, Eye, EyeOff,
  Languages, AlertCircle, Settings2,
} from "lucide-react";
import Select from "react-select";
import Register from "./Register";

const LANGUAGES = [
  { value: "en-US", label: "English", flag: "🇺🇸" },
  { value: "fr", label: "French", flag: "🇫🇷" },
  { value: "es", label: "Spanish", flag: "🇪🇸" },
  { value: "de", label: "German", flag: "🇩🇪" },
  { value: "it", label: "Italian", flag: "🇮🇹" },
  { value: "pt", label: "Portuguese", flag: "🇵🇹" },
  { value: "nl", label: "Dutch", flag: "🇳🇱" },
  { value: "ru", label: "Russian", flag: "🇷🇺" },
  { value: "zh-CN", label: "Chinese", flag: "🇨🇳" },
  { value: "ja", label: "Japanese", flag: "🇯🇵" },
  { value: "ko", label: "Korean", flag: "🇰🇷" },
  { value: "ar", label: "Arabic", flag: "🇸🇦" },
];

const MODELS = [
  { value: "gpt-4o-mini", label: "GPT-4o mini (fast)" },
  { value: "gpt-4o", label: "GPT-4o (best)" },
];

const selectStyles = {
  control: (b, s) => ({
    ...b,
    borderColor: s.isFocused ? "#4338ca" : "#e2e8f0",
    boxShadow: s.isFocused ? "0 0 0 2px rgba(67,56,202,0.2)" : "none",
    borderRadius: "0.5rem",
    minHeight: "40px",
    fontSize: "0.875rem",
  }),
  option: (b, s) => ({
    ...b,
    backgroundColor: s.isSelected ? "#4338ca" : s.isFocused ? "#eef2ff" : "white",
    color: s.isSelected ? "white" : "#334155",
    fontSize: "0.875rem",
  }),
};

const Transcriber = ({ usageCount, setUsageCount, isRegistered, onRegisterSuccess }) => {
  // Recording
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [audioUrl, setAudioUrl] = useState("");
  const [latestTranscription, setLatestTranscription] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [timeLeft, setTimeLeft] = useState(60);
  const [showRegisterModal, setShowRegisterModal] = useState(false);

  // Active mode: "audio" | "translate" | "chatgpt"
  const [activeMode, setActiveMode] = useState("audio");

  // Audio-only history
  const [audioHistory, setAudioHistory] = useState([]);
  // [{id, transcription, audioUrl, timestamp}]

  // Translate
  const [translateLang, setTranslateLang] = useState(LANGUAGES[0]);
  const [translateHistory, setTranslateHistory] = useState([]);
  // [{id, transcription, translation, audioUrl, lang, timestamp}]
  const [isTranslating, setIsTranslating] = useState(false);
  const [translateError, setTranslateError] = useState("");
  const [speakingId, setSpeakingId] = useState(null);

  // Chat
  const [chatApiKey, setChatApiKey] = useState("");
  const [showApiKey, setShowApiKey] = useState(false);
  const [chatModel, setChatModel] = useState("gpt-4o-mini");
  const [chatHistory, setChatHistory] = useState([]);
  // [{role, content, audioUrl?}]
  const [isChatLoading, setIsChatLoading] = useState(false);
  const [chatError, setChatError] = useState("");
  const [isChatActivated, setIsChatActivated] = useState(false);
  const [showChatConfig, setShowChatConfig] = useState(false);

  // Share
  const [copied, setCopied] = useState(false);
  const [sharedStatus, setSharedStatus] = useState({});
  const [copiedEntry, setCopiedEntry] = useState(null); // {id, type}

  // Refs to avoid stale closures in recorder.onstop
  const activeModeRef = useRef("audio");
  const translateLangRef = useRef(LANGUAGES[0]);
  const chatApiKeyRef = useRef("");
  const chatModelRef = useRef("gpt-4o-mini");
  const chatHistoryRef = useRef([]);
  const audioChunks = useRef([]);
  const timerRef = useRef(null);
  const voicesRef = useRef([]);
  const chatEndRef = useRef(null);

  useEffect(() => { activeModeRef.current = activeMode; }, [activeMode]);
  useEffect(() => { translateLangRef.current = translateLang; }, [translateLang]);
  useEffect(() => { chatApiKeyRef.current = chatApiKey; setChatError(""); }, [chatApiKey]);
  useEffect(() => { chatModelRef.current = chatModel; }, [chatModel]);

  // Re-translate all history entries when language changes
  useEffect(() => {
    if (translateHistory.length === 0) return;
    setIsTranslating(true);
    const lang = translateLang;
    const retranslateAll = async () => {
      const updated = await Promise.all(
        translateHistory.map(async (entry) => {
          try {
            const url = `https://translate.googleapis.com/translate_a/single?client=gtx&sl=auto&tl=${lang.value}&dt=t&q=${encodeURIComponent(entry.transcription)}`;
            const r = await fetch(url);
            if (!r.ok) return entry;
            const data = await r.json();
            if (data?.[0]) {
              return { ...entry, translation: data[0].map((s) => s[0]).join(""), lang };
            }
          } catch (_) {}
          return entry;
        })
      );
      setTranslateHistory(updated);
      setIsTranslating(false);
    };
    retranslateAll();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [translateLang]);

  useEffect(() => {
    if (!window.speechSynthesis) return;
    const load = () => { voicesRef.current = window.speechSynthesis.getVoices(); };
    load();
    window.speechSynthesis.onvoiceschanged = load;
    return () => { window.speechSynthesis?.cancel(); window.speechSynthesis.onvoiceschanged = null; };
  }, []);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [chatHistory]);

  // ── Translate trigger (called from onstop) ──
  const triggerTranslate = async (text, blobUrl) => {
    setIsTranslating(true);
    setTranslateError("");
    const lang = translateLangRef.current;
    const url = `https://translate.googleapis.com/translate_a/single?client=gtx&sl=auto&tl=${lang.value}&dt=t&q=${encodeURIComponent(text)}`;
    try {
      const r = await fetch(url);
      if (!r.ok) throw new Error("Translation service unavailable");
      const data = await r.json();
      if (data?.[0]) {
        const translation = data[0].map((s) => s[0]).join("");
        setTranslateHistory((prev) => [
          { id: Date.now(), transcription: text, translation, audioUrl: blobUrl, lang, timestamp: new Date() },
          ...prev,
        ]);
      } else throw new Error("Invalid response");
    } catch (e) {
      setTranslateError(e.message || "Translation failed");
    }
    setIsTranslating(false);
  };

  // ── Chat trigger (called from onstop) ──
  const triggerChat = async (text, blobUrl) => {
    if (!chatApiKeyRef.current.trim()) {
      setChatError("Please enter your OpenAI API key and activate ChatGPT before recording.");
      return;
    }
    const userMsg = { role: "user", content: text, audioUrl: blobUrl };
    const newHistory = [...chatHistoryRef.current, userMsg];
    setChatHistory(newHistory);
    chatHistoryRef.current = newHistory;
    setChatError("");
    setIsChatLoading(true);
    try {
      const r = await fetch("/api/llm", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          messages: [
            { role: "system", content: "You are a helpful assistant." },
            ...newHistory.map((m) => ({ role: m.role, content: m.content })),
          ],
          model: chatModelRef.current,
          api_key: chatApiKeyRef.current.trim().replace(/^=+/, ""),
        }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || `Error ${r.status}`);
      }
      const data = await r.json();
      const finalHistory = [...newHistory, { role: "assistant", content: data.response }];
      setChatHistory(finalHistory);
      chatHistoryRef.current = finalHistory;
    } catch (e) {
      setChatError(e.message);
    }
    setIsChatLoading(false);
  };

  // ── Recording ──
  const handleStartRecording = () => {
    if (!isRegistered && usageCount >= 3) { setShowRegisterModal(true); return; }
    navigator.mediaDevices.getUserMedia({ audio: true }).then((stream) => {
      const recorder = new MediaRecorder(stream);
      setMediaRecorder(recorder);
      setIsRecording(true);
      setAudioUrl("");
      setLatestTranscription("");
      audioChunks.current = [];
      setTimeLeft(60);
      recorder.start(250);

      timerRef.current = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) { clearInterval(timerRef.current); recorder.stop(); return 0; }
          return prev - 1;
        });
      }, 1000);

      recorder.ondataavailable = (e) => audioChunks.current.push(e.data);

      recorder.onstop = () => {
        setIsRecording(false);
        setIsProcessing(true);
        const mimeType = recorder.mimeType || "audio/webm";
        const ext = mimeType.includes("ogg") ? "ogg" : mimeType.includes("mp4") ? "mp4" : "webm";
        const blob = new Blob(audioChunks.current, { type: mimeType });
        const blobUrl = URL.createObjectURL(blob);
        setAudioUrl(blobUrl);
        const fd = new FormData();
        fd.append("audio", blob, `recording.${ext}`);
        fetch("/api/transcribe", { method: "POST", body: fd })
          .then((r) => { if (!r.ok) throw new Error(); return r.json(); })
          .then((d) => {
            const text = d.transcription || "Error: Unable to transcribe";
            setLatestTranscription(text);
            if (!isRegistered && d.transcription) setUsageCount((p) => p + 1);
            setIsProcessing(false);
            // Fire active workflow
            const mode = activeModeRef.current;
            if (mode === "audio") {
              setAudioHistory((prev) => [
                { id: Date.now(), transcription: text, audioUrl: blobUrl, timestamp: new Date() },
                ...prev,
              ]);
            } else if (mode === "translate") triggerTranslate(text, blobUrl);
            else if (mode === "chatgpt") triggerChat(text, blobUrl);
          })
          .catch(() => {
            setLatestTranscription("Error: Unable to transcribe audio");
            setIsProcessing(false);
          });
      };
    }).catch(() => alert("Unable to access microphone."));
  };

  const handleStopRecording = () => {
    clearInterval(timerRef.current);
    setTimeLeft(60);
    if (mediaRecorder) mediaRecorder.stop();
  };

  // ── TTS ──
  const handleSpeak = (entry) => {
    if (!window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    const utt = new SpeechSynthesisUtterance(entry.translation);
    utt.lang = entry.lang.value;
    utt.rate = 0.9;
    const voices = voicesRef.current;
    const base = entry.lang.value.split("-")[0];
    const voice =
      voices.find((v) => v.lang === entry.lang.value && v.name.includes("Google")) ||
      voices.find((v) => v.lang.startsWith(base) && v.name.includes("Google")) ||
      voices.find((v) => v.lang === entry.lang.value) ||
      voices.find((v) => v.lang.startsWith(base));
    if (voice) utt.voice = voice;
    utt.onstart = () => setSpeakingId(entry.id);
    utt.onend = () => setSpeakingId(null);
    utt.onerror = () => setSpeakingId(null);
    window.speechSynthesis.speak(utt);
  };

  const handleSpeakText = (id, text) => {
    if (!window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    const utt = new SpeechSynthesisUtterance(text);
    utt.rate = 0.9;
    utt.onstart = () => setSpeakingId(id);
    utt.onend = () => setSpeakingId(null);
    utt.onerror = () => setSpeakingId(null);
    window.speechSynthesis.speak(utt);
  };

  const handleStopSpeaking = () => { window.speechSynthesis?.cancel(); setSpeakingId(null); };

  // ── ChatGPT activation ──
  const handleActivateChat = () => {
    if (!chatApiKey.trim()) return;
    setIsChatActivated(true);
    setShowChatConfig(false);
    setActiveMode("chatgpt");
    setChatError("");
  };

  const handleDeactivateChat = () => {
    setIsChatActivated(false);
    setShowChatConfig(false);
    setActiveMode("audio");
  };

  // ── Share ──
  const handleCopy = () => {
    navigator.clipboard.writeText(latestTranscription).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleCopyEntry = (id, text, type) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedEntry({ id, type });
      setTimeout(() => setCopiedEntry(null), 2000);
    });
  };

  const handleShare = (platform) => {
    const enc = encodeURIComponent(latestTranscription);
    if (platform === "whatsapp") {
      window.location.href = `whatsapp://send?text=${enc}`;
      setTimeout(() => { if (document.hasFocus()) window.open(`https://wa.me/?text=${enc}`, "_blank"); }, 500);
    } else if (platform === "telegram") {
      window.open(`https://t.me/share/url?text=${enc}`, "_blank");
    } else if (platform === "email") {
      window.open(`mailto:?subject=Scribify Transcription&body=${enc}`);
    }
    setSharedStatus((p) => ({ ...p, [platform]: true }));
    setTimeout(() => setSharedStatus((p) => ({ ...p, [platform]: false })), 2500);
  };

  const hasHistory = audioHistory.length > 0 || translateHistory.length > 0 || chatHistory.length > 0;
  const formatLang = ({ label, flag }) => (
    <div className="flex items-center gap-2"><span>{flag}</span><span>{label}</span></div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 pb-20">

      {/* Register modal */}
      {showRegisterModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={() => setShowRegisterModal(false)} />
          <div className="relative z-10 w-full max-w-md bg-white rounded-3xl shadow-2xl overflow-hidden border border-slate-200">
            <button onClick={() => setShowRegisterModal(false)} className="absolute top-4 right-4 p-2 hover:bg-slate-100 rounded-full text-slate-400 z-20">
              <X className="w-5 h-5" />
            </button>
            <Register onRegisterSuccess={(u) => { onRegisterSuccess(u); setShowRegisterModal(false); }} />
          </div>
        </div>
      )}

      <div className="container mx-auto px-4 py-8 max-w-5xl">

        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-5xl md:text-6xl font-bold text-slate-900 mb-3">Scribify</h1>
          <p className="text-xl text-slate-600 font-light italic">Speak It Out, Anytime, Anywhere!</p>
          <div className="mt-4">
            {!isRegistered ? (
              <span className="px-4 py-1.5 bg-emerald-100 text-emerald-700 rounded-full text-sm font-medium border border-emerald-200">
                Free trials left: {Math.max(0, 3 - usageCount)} / 3
              </span>
            ) : (
              <span className="px-4 py-1.5 bg-blue-100 text-blue-700 rounded-full text-sm font-medium border border-blue-200">
                Unlimited Member Mode ✨
              </span>
            )}
          </div>
        </div>

        {/* ── TOP ROW: 3 nodes ── */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">

          {/* 1. Audio Input */}
          <div className="bg-white rounded-2xl border-2 border-emerald-200 shadow-md overflow-hidden flex flex-col">
            <div className="flex items-center justify-between px-4 py-3 bg-emerald-500">
              <div className="flex items-center gap-3">
                <div className="w-7 h-7 rounded-lg bg-white/20 flex items-center justify-center flex-shrink-0">
                  <Mic className="w-4 h-4 text-white" />
                </div>
                <div>
                  <p className="font-bold text-white text-sm">Audio Input</p>
                  <p className="text-xs text-emerald-100">Whisper transcription</p>
                </div>
              </div>
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                activeMode === "audio" ? "bg-emerald-400/30 text-white" :
                activeMode === "translate" ? "bg-blue-400/30 text-white" : "bg-purple-400/30 text-white"
              }`}>
                {activeMode === "audio" ? "🎙 Audio" : activeMode === "translate" ? "🌐 Translate" : "🤖 ChatGPT"}
              </span>
            </div>
            <div className="p-4 space-y-3 flex-1 min-h-[11rem]">
              {isRecording && (
                <div className="flex flex-col items-center gap-1.5">
                  <div className="flex items-center gap-2">
                    <div className="flex gap-0.5">
                      <div className="w-1 h-5 bg-red-500 rounded-full animate-pulse" />
                      <div className="w-1 h-7 bg-red-500 rounded-full animate-pulse delay-75" />
                      <div className="w-1 h-5 bg-red-500 rounded-full animate-pulse delay-150" />
                    </div>
                    <span className="text-red-500 text-xs font-bold uppercase tracking-wider">Recording</span>
                  </div>
                  <span className={`font-mono font-bold text-lg ${timeLeft <= 10 ? "text-red-600" : "text-slate-700"}`}>
                    0:{String(timeLeft).padStart(2, "0")}
                  </span>
                  <div className="w-full h-1.5 bg-slate-200 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all duration-1000 ${timeLeft <= 10 ? "bg-red-500" : "bg-emerald-500"}`}
                      style={{ width: `${(timeLeft / 60) * 100}%` }}
                    />
                  </div>
                </div>
              )}
              <p className="text-xs text-center text-slate-400 font-medium">
                {activeMode === "audio" && "Record audio to transcribe"}
                {activeMode === "translate" && "Record audio to auto-translate"}
                {activeMode === "chatgpt" && "Record audio to chat with GPT"}
              </p>
              <div className="flex gap-2">
                <button onClick={handleStartRecording} disabled={isRecording}
                  className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl font-semibold text-sm transition-all ${
                    isRecording ? "bg-gray-200 text-gray-400 cursor-not-allowed" : "bg-emerald-500 hover:bg-emerald-600 text-white active:scale-95"
                  }`}
                >
                  <Mic className="w-4 h-4" /> Record
                </button>
                <button onClick={handleStopRecording} disabled={!isRecording}
                  className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl font-semibold text-sm transition-all ${
                    !isRecording ? "bg-gray-200 text-gray-400 cursor-not-allowed" : "bg-red-500 hover:bg-red-600 text-white active:scale-95"
                  }`}
                >
                  <Square className="w-4 h-4" /> Stop
                </button>
              </div>
              {audioUrl && <audio controls src={audioUrl} className="w-full h-8" />}
              {isProcessing && (
                <div className="flex items-center gap-2 py-1 text-emerald-600">
                  <div className="animate-spin h-4 w-4 border-2 border-emerald-500 border-t-transparent rounded-full" />
                  <span className="text-sm font-medium">Transcribing...</span>
                </div>
              )}
              {!isRecording && !audioUrl && !latestTranscription && (
                <p className="text-slate-400 text-xs text-center py-1">Press Record to start</p>
              )}
            </div>
          </div>

          {/* 2. Translate */}
          <div className={`bg-white rounded-2xl border-2 shadow-md overflow-hidden transition-all flex flex-col ${
            activeMode === "translate" ? "border-indigo-300 ring-2 ring-indigo-100" : "border-slate-200"
          }`}>
            <div className={`flex items-center justify-between px-4 py-3 ${activeMode === "translate" ? "bg-indigo-700" : "bg-slate-400"}`}>
              <div className="flex items-center gap-2">
                <span className="text-xl">🌐</span>
                <div>
                  <p className="font-bold text-white text-sm">Translate</p>
                  <p className="text-xs text-white/70">Auto-translate on record</p>
                </div>
              </div>
              {activeMode === "translate" && (
                <span className="px-2 py-0.5 bg-white/20 rounded-full text-xs text-white font-medium">Active ✓</span>
              )}
            </div>
            <div className="p-4 space-y-3 flex-1 min-h-[11rem]">
              <Select
                value={translateLang}
                onChange={setTranslateLang}
                options={LANGUAGES}
                formatOptionLabel={formatLang}
                isSearchable={false}
                menuPlacement="auto"
                menuPosition="fixed"
                styles={selectStyles}
              />
              {activeMode !== "translate" ? (
                <button
                  onClick={() => { setActiveMode("translate"); setIsChatActivated(false); setShowChatConfig(false); }}
                  className="w-full flex items-center justify-center gap-1.5 py-2.5 rounded-xl font-semibold text-sm bg-indigo-700 hover:bg-indigo-800 text-white transition-all active:scale-95"
                >
                  <Languages className="w-4 h-4" /> Activate Translate
                </button>
              ) : (
                <button
                  onClick={() => setActiveMode("audio")}
                  className="w-full py-2 rounded-xl text-sm font-medium bg-slate-100 hover:bg-slate-200 text-slate-600 transition-all"
                >
                  Deactivate
                </button>
              )}
              {translateError && (
                <div className="flex items-center gap-1.5 text-red-600 text-xs">
                  <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" /> {translateError}
                </div>
              )}
            </div>
          </div>

          {/* 3. ChatGPT */}
          <div className={`bg-white rounded-2xl border-2 shadow-md overflow-hidden transition-all flex flex-col ${
            isChatActivated ? "border-purple-300 ring-2 ring-purple-100" : "border-slate-200"
          }`}>
            <div className={`flex items-center justify-between px-4 py-3 ${isChatActivated ? "bg-purple-600" : "bg-slate-400"}`}>
              <div className="flex items-center gap-2">
                <span className="text-xl">🤖</span>
                <div>
                  <p className="font-bold text-white text-sm">ChatGPT</p>
                  <p className="text-xs text-white/70">Audio-driven conversation</p>
                </div>
              </div>
              {isChatActivated && (
                <span className="px-2 py-0.5 bg-white/20 rounded-full text-xs text-white font-medium">Active ✓</span>
              )}
            </div>
            <div className="p-4 space-y-3 flex-1 min-h-[11rem]">
              {!isChatActivated ? (
                <>
                  <div className="relative">
                    <input
                      type={showApiKey ? "text" : "password"}
                      value={chatApiKey}
                      onChange={(e) => setChatApiKey(e.target.value)}
                      placeholder="sk-..."
                      className="w-full px-3 py-2 pr-9 rounded-lg border border-slate-200 text-sm font-mono focus:border-purple-400 focus:ring-2 focus:ring-purple-100 outline-none"
                    />
                    <button type="button" onClick={() => setShowApiKey((v) => !v)} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                      {showApiKey ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                    </button>
                  </div>
                  <select
                    value={chatModel}
                    onChange={(e) => setChatModel(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:border-purple-400 outline-none bg-white"
                  >
                    {MODELS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                  </select>
                  <button
                    onClick={handleActivateChat}
                    disabled={!chatApiKey.trim()}
                    className={`w-full flex items-center justify-center gap-1.5 py-2.5 rounded-xl font-semibold text-sm transition-all ${
                      !chatApiKey.trim() ? "bg-gray-200 text-gray-400 cursor-not-allowed" : "bg-purple-500 hover:bg-purple-600 text-white active:scale-95"
                    }`}
                  >
                    <Bot className="w-4 h-4" /> Activate ChatGPT
                  </button>
                </>
              ) : (
                <div className="space-y-3">
                  {showChatConfig ? (
                    <>
                      <div className="relative">
                        <input
                          type={showApiKey ? "text" : "password"}
                          value={chatApiKey}
                          onChange={(e) => setChatApiKey(e.target.value)}
                          placeholder="sk-..."
                          className="w-full px-3 py-2 pr-9 rounded-lg border border-slate-200 text-sm font-mono focus:border-purple-400 focus:ring-2 focus:ring-purple-100 outline-none"
                        />
                        <button type="button" onClick={() => setShowApiKey((v) => !v)} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                          {showApiKey ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                        </button>
                      </div>
                      <select
                        value={chatModel}
                        onChange={(e) => setChatModel(e.target.value)}
                        className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:border-purple-400 outline-none bg-white"
                      >
                        {MODELS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                      </select>
                      <button
                        onClick={handleActivateChat}
                        disabled={!chatApiKey.trim()}
                        className={`w-full flex items-center justify-center gap-1.5 py-2.5 rounded-xl font-semibold text-sm transition-all ${
                          !chatApiKey.trim() ? "bg-gray-200 text-gray-400 cursor-not-allowed" : "bg-purple-500 hover:bg-purple-600 text-white active:scale-95"
                        }`}
                      >
                        <Bot className="w-4 h-4" /> Update & Activate
                      </button>
                    </>
                  ) : (
                    <>
                      <p className="text-xs text-slate-500">Model: <span className="font-mono">{chatModel}</span></p>
                      <p className="text-xs text-slate-400">Recording mode: <span className={`font-medium ${activeMode === "chatgpt" ? "text-purple-600" : "text-slate-500"}`}>{activeMode === "chatgpt" ? "ChatGPT ✓" : activeMode}</span></p>
                    </>
                  )}
                  <button
                    onClick={handleDeactivateChat}
                    className="w-full py-2 rounded-xl text-sm font-medium bg-slate-100 hover:bg-slate-200 text-slate-600 transition-all"
                  >
                    Deactivate
                  </button>
                  <button
                    onClick={() => setShowChatConfig((v) => !v)}
                    className="w-full flex items-center justify-center gap-1.5 py-2 rounded-xl text-sm font-medium bg-slate-100 hover:bg-slate-200 text-slate-600 transition-all"
                  >
                    <Settings2 className="w-3.5 h-3.5" /> {showChatConfig ? "Hide Settings" : "Settings"}
                  </button>
                </div>
              )}
              {chatError && (
                <div className="flex items-start gap-1.5 text-red-600 text-xs">
                  <AlertCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" /> {chatError}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ── CENTRAL HUB ── */}
        {isProcessing && !hasHistory && (
          <div className="flex items-center justify-center gap-3 py-12">
            <div className="animate-spin h-6 w-6 border-2 border-emerald-500 border-t-transparent rounded-full" />
            <span className="text-emerald-700 font-medium">Transcribing your audio...</span>
          </div>
        )}

        {(isProcessing && hasHistory) && (
          <div className="flex items-center gap-2 px-4 py-3 mb-4 bg-emerald-50 border border-emerald-200 rounded-xl">
            <div className="animate-spin h-4 w-4 border-2 border-emerald-500 border-t-transparent rounded-full flex-shrink-0" />
            <span className="text-emerald-700 text-sm font-medium">
              {activeMode === "audio" ? "Transcribing..." : activeMode === "translate" ? "Transcribing & translating..." : "Transcribing & sending to ChatGPT..."}
            </span>
          </div>
        )}

        {hasHistory && (
          <div className="bg-white rounded-2xl border border-slate-200 shadow-xl overflow-hidden">

            {/* Hub tabs */}
            <div className="flex border-b border-slate-100">
              {[
                { id: "audio", label: "Audio Feed", emoji: "🎙", count: audioHistory.length, activeBg: "bg-emerald-500" },
                { id: "translate", label: "Translate", emoji: "🌐", count: translateHistory.length, activeBg: "bg-indigo-700" },
                { id: "chatgpt", label: "ChatGPT", emoji: "🤖", count: chatHistory.length, activeBg: "bg-purple-600" },
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveMode(tab.id)}
                  className={`flex-1 flex items-center justify-center gap-1.5 py-3.5 text-sm font-semibold transition-all ${
                    activeMode === tab.id
                      ? `${tab.activeBg} text-white`
                      : "text-slate-400 hover:bg-slate-50 hover:text-slate-600"
                  }`}
                >
                  <span>{tab.emoji}</span>
                  <span className="hidden sm:inline">{tab.label}</span>
                  {tab.count > 0 && (
                    <span className={`px-1.5 py-0.5 rounded-full text-xs font-bold ${
                      activeMode === tab.id ? "bg-white/25 text-white" : "bg-slate-100 text-slate-500"
                    }`}>
                      {tab.count}
                    </span>
                  )}
                </button>
              ))}
            </div>

            <div className="p-6 space-y-4">

              {/* ── Audio Feed ── */}
              {activeMode === "audio" && (
                <>
                  {audioHistory.map((entry, i) => (
                    <div key={entry.id} className="border border-slate-200 rounded-xl overflow-hidden">
                      <div className="flex items-center justify-between px-4 py-2 bg-slate-50 border-b border-slate-100">
                        <span className="text-xs text-slate-500 flex items-center gap-1.5 font-medium">
                          <Mic className="w-3 h-3 text-emerald-500" />
                          Recording {audioHistory.length - i}
                        </span>
                        <span className="text-xs text-slate-400">
                          {entry.timestamp.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                        </span>
                      </div>
                      <div className="p-4 space-y-3">
                        <div className="space-y-1">
                          <div className="flex items-center justify-between">
                            <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Transcription</span>
                            <div className="flex items-center gap-1.5">
                              <button
                                onClick={() => handleCopyEntry(entry.id, entry.transcription, "transcription")}
                                className={`flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium transition-all ${
                                  copiedEntry?.id === entry.id && copiedEntry?.type === "transcription"
                                    ? "bg-emerald-100 text-emerald-700"
                                    : "bg-slate-100 text-slate-500 hover:bg-slate-200"
                                }`}
                              >
                                {copiedEntry?.id === entry.id && copiedEntry?.type === "transcription"
                                  ? <><Check className="w-3 h-3" /> Copied</>
                                  : <><Copy className="w-3 h-3" /> Copy</>
                                }
                              </button>
                              <button
                                onClick={() => speakingId === entry.id ? handleStopSpeaking() : handleSpeakText(entry.id, entry.transcription)}
                                className={`flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium flex-shrink-0 transition-all ${
                                  speakingId === entry.id
                                    ? "bg-red-100 text-red-600 hover:bg-red-200"
                                    : "bg-emerald-100 text-emerald-700 hover:bg-emerald-200"
                                }`}
                              >
                                <Volume2 className="w-3 h-3" />
                                {speakingId === entry.id ? "Stop" : "Listen"}
                              </button>
                            </div>
                          </div>
                          <p className="text-sm text-slate-700 leading-relaxed">{entry.transcription}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </>
              )}

              {/* ── Translate History ── */}
              {activeMode === "translate" && (
                <>
                  {isTranslating && (
                    <div className="flex items-center gap-2 py-3 px-4 bg-indigo-50 border border-indigo-100 rounded-xl">
                      <div className="animate-spin h-4 w-4 border-2 border-indigo-600 border-t-transparent rounded-full" />
                      <span className="text-indigo-700 text-sm">Translating to {translateLang.flag} {translateLang.label}...</span>
                    </div>
                  )}
                  {translateHistory.map((entry, i) => (
                    <div key={entry.id} className="border border-slate-200 rounded-xl overflow-hidden">
                      {/* Card header */}
                      <div className="flex items-center justify-between px-4 py-2 bg-slate-50 border-b border-slate-100">
                        <span className="text-xs text-slate-500 flex items-center gap-1.5 font-medium">
                          <Mic className="w-3 h-3 text-emerald-500" />
                          Recording {translateHistory.length - i}
                        </span>
                        <span className="text-xs text-slate-400">
                          {entry.timestamp.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                        </span>
                      </div>

                      <div className="p-4 space-y-3">
                        {/* Transcription */}
                        <div className="space-y-1">
                          <div className="flex items-center justify-between">
                            <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Transcription</span>
                            <div className="flex items-center gap-1.5">
                              <button
                                onClick={() => handleCopyEntry(entry.id, entry.transcription, "transcription")}
                                className={`flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium transition-all ${
                                  copiedEntry?.id === entry.id && copiedEntry?.type === "transcription"
                                    ? "bg-emerald-100 text-emerald-700"
                                    : "bg-slate-100 text-slate-500 hover:bg-slate-200"
                                }`}
                              >
                                {copiedEntry?.id === entry.id && copiedEntry?.type === "transcription"
                                  ? <><Check className="w-3 h-3" /> Copied</>
                                  : <><Copy className="w-3 h-3" /> Copy</>
                                }
                              </button>
                              <button
                                onClick={() => speakingId === `${entry.id}-src` ? handleStopSpeaking() : handleSpeakText(`${entry.id}-src`, entry.transcription)}
                                className={`flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium flex-shrink-0 transition-all ${
                                  speakingId === `${entry.id}-src`
                                    ? "bg-red-100 text-red-600 hover:bg-red-200"
                                    : "bg-emerald-100 text-emerald-700 hover:bg-emerald-200"
                                }`}
                              >
                                <Volume2 className="w-3 h-3" />
                                {speakingId === `${entry.id}-src` ? "Stop" : "Listen"}
                              </button>
                            </div>
                          </div>
                          <p className="text-sm text-slate-600 leading-relaxed">{entry.transcription}</p>
                        </div>

                        <hr className="border-slate-200" />

                        {/* Translation */}
                        <div className="space-y-1">
                          <div className="flex items-center justify-between">
                            <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                              {entry.lang.flag} {entry.lang.label}
                            </span>
                            <div className="flex items-center gap-1.5">
                              <button
                                onClick={() => handleCopyEntry(entry.id, entry.translation, "translation")}
                                className={`flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium transition-all ${
                                  copiedEntry?.id === entry.id && copiedEntry?.type === "translation"
                                    ? "bg-emerald-100 text-emerald-700"
                                    : "bg-slate-100 text-slate-500 hover:bg-slate-200"
                                }`}
                              >
                                {copiedEntry?.id === entry.id && copiedEntry?.type === "translation"
                                  ? <><Check className="w-3 h-3" /> Copied</>
                                  : <><Copy className="w-3 h-3" /> Copy</>
                                }
                              </button>
                              <button
                                onClick={() => speakingId === entry.id ? handleStopSpeaking() : handleSpeak(entry)}
                                className={`flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium flex-shrink-0 transition-all ${
                                  speakingId === entry.id
                                    ? "bg-red-100 text-red-600 hover:bg-red-200"
                                    : "bg-indigo-100 text-indigo-700 hover:bg-indigo-200"
                                }`}
                              >
                                <Volume2 className="w-3 h-3" />
                                {speakingId === entry.id ? "Stop" : "Listen"}
                              </button>
                            </div>
                          </div>
                          <p className="text-sm text-slate-800 leading-relaxed font-medium">{entry.translation}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </>
              )}

              {/* ── ChatGPT Conversation ── */}
              {activeMode === "chatgpt" && (
                <div className="space-y-3 max-h-[32rem] overflow-y-auto pr-1">
                  {chatHistory.map((msg, i) => (
                    <div key={i} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
                      {msg.role === "user" ? (
                        <div className="max-w-[80%] space-y-1.5">
                          <div className="flex items-center justify-end gap-1.5">
                            <Mic className="w-3 h-3 text-purple-400" />
                            <span className="text-xs text-slate-400">You (audio)</span>
                          </div>
                          <div className="bg-purple-500 text-white px-4 py-2.5 rounded-2xl rounded-br-sm text-sm leading-relaxed">
                            {msg.content}
                          </div>
                        </div>
                      ) : (
                        <div className="max-w-[80%] space-y-1.5">
                          <div className="flex items-center gap-1.5">
                            <Bot className="w-3 h-3 text-purple-600" />
                            <span className="text-xs text-slate-400">ChatGPT</span>
                          </div>
                          <div className="bg-slate-100 text-slate-800 px-4 py-2.5 rounded-2xl rounded-bl-sm text-sm leading-relaxed prose prose-sm max-w-none prose-headings:font-semibold prose-strong:font-semibold prose-ol:pl-4 prose-ul:pl-4 prose-li:my-0.5">
                            <ReactMarkdown>{msg.content}</ReactMarkdown>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                  {isChatLoading && (
                    <div className="flex justify-start">
                      <div className="bg-slate-100 px-4 py-3 rounded-2xl rounded-bl-sm flex items-center gap-1">
                        <div className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" />
                        <div className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce delay-75" />
                        <div className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce delay-150" />
                      </div>
                    </div>
                  )}
                  <div ref={chatEndRef} />
                </div>
              )}

              {/* ── Share buttons ── */}
              <div className="flex flex-wrap justify-end gap-2 pt-3 border-t border-slate-100">
                {[
                  { id: "whatsapp", label: "WhatsApp", icon: <Send className="w-3.5 h-3.5" />, cls: "bg-green-500 hover:bg-green-600", activeCls: "bg-green-100 text-green-700" },
                  { id: "telegram", label: "Telegram", icon: <Send className="w-3.5 h-3.5" />, cls: "bg-sky-500 hover:bg-sky-600", activeCls: "bg-sky-100 text-sky-700" },
                  { id: "email", label: "Email", icon: <Mail className="w-3.5 h-3.5" />, cls: "bg-orange-500 hover:bg-orange-600", activeCls: "bg-orange-100 text-orange-700" },
                ].map(({ id, label, icon, cls, activeCls }) => (
                  <button key={id} onClick={() => handleShare(id)}
                    className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                      sharedStatus[id] ? activeCls : `${cls} text-white`
                    }`}
                  >
                    {icon} {sharedStatus[id] ? "✓" : label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Empty state */}
        {!hasHistory && !isProcessing && (
          <div className="text-center py-16">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-emerald-100 border border-emerald-200 mb-4 text-emerald-500">
              <Mic className="w-10 h-10" />
            </div>
            <p className="text-slate-500 font-medium mb-1">
              {activeMode === "audio" ? "🎙 Audio Input only" : activeMode === "translate" ? "🌐 Auto-Translate active" : "🤖 ChatGPT active"}
            </p>
            <p className="text-slate-400 text-sm">Press Record — your audio will be transcribed{activeMode !== "audio" ? " and processed automatically" : ""}</p>
          </div>
        )}

      </div>
    </div>
  );
};

export default Transcriber;
